<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <form @submit="createIndex" class="flex flex-col h-full">
      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full">
          <div data-testid="sticky-create-header" class="sticky top-0 z-20 bg-white px-8 pt-7 pb-4 border-solid border-b border-0 border-gray-border">
            <h3 class="font-semibold">
              Create New Knowledge Base<transition name="group-suffix"><span v-if="isMarketScope" data-testid="group-suffix" class="inline-block ml-1">Group</span></transition>
            </h3>

            <div class="mt-4">
              <div data-testid="progress-summary"
                   class="text-sm mb-2"
                   :class="completedSteps > 0 ? 'text-success' : 'text-gray'"
              >
                <span class="font-medium"><span data-testid="completed-steps-count" class="inline-block w-2 text-right mr-px">{{ completedSteps }}</span>/3 steps completed</span>
              </div>
              <div class="h-2 rounded-full bg-gray-a10 overflow-hidden"
                   role="progressbar"
                   aria-label="Knowledge base creation progress"
                   :aria-valuenow="completedSteps"
                   aria-valuemin="0"
                   aria-valuemax="3"
              >
                <div class="knowledge-base-progress h-full bg-success rounded-full"
                     :style="{ width: `${progressPercentage}%` }"
                />
              </div>
            </div>
          </div>

        <div class="mt-3">
          <with-error-message :show-error="isIndexNameError" class="border-solid border-b border-0 border-gray-border px-8 pb-5">
            <base-label id="indexName" label="Knowledge Base Name" :label-classes="'font-medium'"/>
            <base-input id="indexName"
                        name="indexName"
                        placeholder="e.g. Sales campaign"
                        variant="editor"
                        type="string"
                        class="w-full border-gray-border mt-1 h-10"
                        :class="{'border-focus-error': isIndexNameError}"
                        v-model.trim="indexName"
            />
          </with-error-message>

          <transition name="step-reveal">
            <div v-if="isScopeStepShown">
              <div class="px-8 py-5 border-solid border-b border-0 border-gray-border">
                <base-label id="indexScope" label="Knowledge Base Scope" :label-classes="'font-medium'"/>
                <base-select id="indexScope"
                             v-model="indexScope"
                             :options="indexScopeOptions"
                             class="w-full mt-1 h-10"
                />
              </div>

              <settings-bot-market-form v-if="isMarketScope"
                                        v-model="marketForm"
                                        content-class="px-8 pb-3"
                                        :existing-only="true"
              />
            </div>
          </transition>

          <div v-if="isSourceStepShown"
               data-testid="source-step"
               class="border-solid border-0 pt-4"
               :class="{ 'mt-3 border-t border-gray-border': isMarketScope }"
          >
            <div class="px-8">
              <with-error-message :show-error="isIndexSourceNameError">
                <base-label id="indexSourceName" label="Source Name" :label-classes="'font-medium'"/>
                <base-input id="indexSourceName"
                            name="indexSourceName"
                            placeholder="e.g. Sales campaign feed"
                            variant="editor"
                            type="string"
                            class="w-full border-gray-border py-px mt-1 h-10"
                            :class="{'border-focus-error': isIndexSourceNameError}"
                            v-model.trim="indexSourceName"
                />
              </with-error-message>

              <div class="mt-5">
                <base-label label="Data source"
                            :label-classes="'font-medium'"
                            id="data-source"
                />
                <BaseToggleGroup class="mt-1" :tabs="formsOptions" v-model="sourceType" />
                <settings-indexes-forms class="mt-4"
                                        :fileSettings.sync="fileSettings"
                                        :active-form.sync="sourceType"
                                        :xmlFeedSettings.sync="xmlFeedSettings"
                                        :webPageSettings.sync="webPageSettings"
                                        :show-field-selection="showFieldSelection"
                                        @update:available-tags="availableTags = $event"
                />
              </div>

              <settings-index-provider v-model="providerForm"
                                       class="mt-5"
              />
            </div>

            <settings-index-scheduler :schedule-params="scheduleParams"
                                      :is-create="true"
                                      @update-schedule-params="updateScheduleParams"
                                      class="mt-6 pt-4 px-8 pb-8 border-solid border-t border-0 border-gray-border"
            />
          </div>
        </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-6 px-8 flex-shrink-0">
        <transition name="create-action" mode="out-in">
          <base-button v-if="!isSourceStepShown"
                       key="add-source"
                       id="addSourceButton"
                       variant="bordered"
                       class="col-span-1 w-full h-full justify-center"
                       :disabled="!isScopeComplete"
                       @click.prevent="revealSourceForm"
          >
            Add source
          </base-button>
          <busy-button v-else
                       key="create"
                       id="createButton"
                       :variant="createButtonVariant"
                       class="col-span-1"
                       :is-loading="isLoading"
          >
            <span class="w-full">{{ createButtonText }}</span>
          </busy-button>
        </transition>
      </div>
    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import BaseButton from '@/components/base-components/BaseButton'
import SettingsBotMarketForm from '@/components/settings/bots/SettingsBotMarketForm.vue'
import SettingsIndexesXmlFeed from '@/components/settings/indexes/SettingsIndexesXmlFeed.vue'
import SettingsIndexScheduler from '@/components/settings/indexes/SettingsIndexScheduler.vue'
import indexGroupService from '@/services/amio/index-group.service'
import indexCreateMixin from '@/utils/index-create.mixin'
import {
  createDefaultMarketForm,
  resolveMarketId
} from '@/utils/market.utils'
import SettingsIndexesForms from '@/components/settings/indexes/SettingsIndexesForms.vue'
import SettingsIndexProvider from '@/components/settings/indexes/SettingsIndexProvider.vue'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import { createDefaultProviderForm } from '@/utils/index-provider.utils'

const INDEX_SCOPE = Object.freeze({
  GLOBAL: 'global',
  MARKET: 'market'
})

export default {
  name: 'SettingsIndexCreate',
  mixins: [indexCreateMixin],
  components: {
    SettingsBotMarketForm,
    SettingsIndexesForms,
    SettingsIndexProvider,
    BaseButton,
    BusyButton,
    SettingsIndexesXmlFeed,
    SettingsIndexScheduler,
    WithErrorMessage
  },
  props: {
    indexes: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      marketForm: createDefaultMarketForm(),
      indexName: '',
      indexScope: INDEX_SCOPE.GLOBAL,
      indexScopeOptions: [
        { value: INDEX_SCOPE.GLOBAL, text: 'Global' },
        { value: INDEX_SCOPE.MARKET, text: 'Market' }
      ],
      isScopeStepShown: false,
      isSourceStepShown: false,
      providerForm: createDefaultProviderForm(),
      availableTags: []
    }
  },
  computed: {
    isIndexNameError() {
      return this.errorDescription?.includes('Knowledge Base Name')
    },

    isMarketScope() {
      return this.indexScope === INDEX_SCOPE.MARKET
    },
    isKnowledgeBaseNameComplete() {
      return !!this.indexName.trim()
    },
    isScopeSelectionValid() {
      return !this.isMarketScope || !!this.marketForm.sourceMarketId
    },
    isScopeComplete() {
      return this.isKnowledgeBaseNameComplete && this.isScopeSelectionValid
    },
    isSourceFormComplete() {
      if (!this.indexSourceName.trim()) return false
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        // A URL is all that is ever required. The element fields are only required once someone has
        // explicitly asked to configure them — gating Save on them whenever they were merely visible
        // made auto-derivation unreachable for internal users.
        const { url, item, itemName, itemId, manualConfig } = this.xmlFeedSettings
        if (!url?.trim()) return false
        if (!manualConfig) return true
        return [item, itemName, itemId].every(value => !!value?.trim())
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        return !!(this.fileSettings.file || this.fileSettings.url?.trim())
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        const { url, headerKey, headerValue } = this.webPageSettings
        if (!url?.trim()) return false
        const isHeaderEnabled = headerKey !== null || headerValue !== null
        return !isHeaderEnabled || !!(headerKey?.trim() && headerValue?.trim())
      }
      return false
    },
    completedSteps() {
      let steps = this.isKnowledgeBaseNameComplete ? 1 : 0
      if (this.isSourceStepShown && this.isScopeSelectionValid) steps += 1
      if (this.isSourceStepShown && this.isSourceFormComplete) steps += 1
      return steps
    },
    progressPercentage() {
      return this.completedSteps / 3 * 100
    },
    // Per-field embed/payload/group-key selection only applies to a Qdrant XML feed once the
    // preview has surfaced the feed's fields.
    showFieldSelection() {
      return this.providerForm.provider === 'qdrant' &&
        this.sourceType === KNOWLEDGE_BASES.XML_FEED &&
        this.availableTags.length > 0
    }
  },
  watch: {
    indexName(indexName) {
      if (indexName.trim()) this.isScopeStepShown = true
    },
    // Drop field-selection state once it no longer applies so another source type cannot carry
    // stale embed/payload/group_key keys.
    showFieldSelection(isShown) {
      if (!isShown) this.updateFieldSelection({ embed_fields: [], payload_fields: {}, group_key: [] })
    }
  },
  methods: {
    revealSourceForm() {
      if (!this.isKnowledgeBaseNameComplete) {
        this.handleError(new Error('Enter a Knowledge Base Name.'))
        return
      }
      if (this.isMarketScope && !this.marketForm.sourceMarketId) {
        this.handleError(new Error('Select a market.'))
        return
      }
      this.isSourceStepShown = true
    },

    createIndex(e) {
      e.preventDefault()
      if (!this.isSourceStepShown) return
      if (this.sourceType === KNOWLEDGE_BASES.XML_FEED) {
        this.createIndexWithXMLSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.FILE) {
        this.createIndexWithFileSource()
      }
      if (this.sourceType === KNOWLEDGE_BASES.WEB_PAGE) {
        this.createIndexWithWebPageSource()
      }
    },

    updateFieldSelection(fieldSelection) {
      this.xmlFeedSettings = { ...this.xmlFeedSettings, fieldSelection }
    },

    async resolveIndexCreateParams() {
      if (this.indexScope === INDEX_SCOPE.GLOBAL) {
        return { indexName: this.indexName, extraParams: { ...this.providerForm }, createdIndexGroupId: null }
      }
      if (!this.marketForm.sourceMarketId) throw new Error('Select a market.')

      const indexGroup = await indexGroupService.create(this.indexName)
      try {
        const { marketId, marketName } = await resolveMarketId(this.marketForm)
        return {
          indexName: marketName,
          extraParams: {
            index_group_id: indexGroup.id,
            market_id: marketId,
            ...this.providerForm
          },
          createdIndexGroupId: indexGroup.id
        }
      } catch (err) {
        await this.rollbackIndexCreate(indexGroup.id)
        throw err
      }
    },

    async rollbackIndexCreate(indexGroupId) {
      if (!indexGroupId) return
      try {
        await indexGroupService.delete(indexGroupId)
      } catch {
        // Best-effort cleanup; the original error is still surfaced to the user
      }
    }
  }
}
</script>

<style scoped>
.knowledge-base-progress {
  transition: width 350ms ease;
}

.step-reveal-enter-active,
.step-reveal-leave-active,
.create-action-enter-active,
.create-action-leave-active,
.group-suffix-enter-active,
.group-suffix-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.step-reveal-enter,
.step-reveal-leave-to,
.create-action-enter,
.create-action-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.group-suffix-enter,
.group-suffix-leave-to {
  opacity: 0;
  transform: translateX(-4px);
}

@media (prefers-reduced-motion: reduce) {
  .knowledge-base-progress,
  .step-reveal-enter-active,
  .step-reveal-leave-active,
  .create-action-enter-active,
  .create-action-leave-active,
  .group-suffix-enter-active,
  .group-suffix-leave-active {
    transition: none;
  }
}
</style>
